import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API } from '../config/api';

// Modal crear/editar gasto. Registro continuo: en modo nuevo, "Guardar y seguir"
// hace POST, limpia monto/descripcion/proveedor/comprobante (conserva fecha,
// moneda, empresa, categoria) y deja el foco en el monto, sin reabrir. "Salir"
// cierra. En modo edicion: "Guardar cambios" (PUT) y cierra.
// Reutilizable desde Gastos.js (tabla y panel de detalle).

const MONEDAS = ['Colones', 'Dólares', 'Euros'];
const EMPRESAS = ['JyA Conusa', 'Cuenta Personal (JCNS)'];
const TIPOS_COMPROBANTE = ['Factura electronica', 'Factura por correo', 'SINPE', 'Sin comprobante', 'Otro'];
const TASAS = [{ v: 13, t: 'IVA 13%' }, { v: 0, t: 'IVA 0% / exento' }];

const hoy = () => new Date().toISOString().slice(0, 10);

const vacio = () => ({
  fecha: hoy(), categoria_id: '', subcategoria_id: '', proyecto_id: '',
  moneda: 'Colones', monto_total: '', tasa_iva: 13, descripcion: '',
  proveedor: '', tipo_comprobante: '', numero_comprobante: '',
  empresa: 'JyA Conusa', estado_pago: 'Pendiente', medio_pago_id: '', fecha_pago: '', notas: ''
});

const inputStyle = {
  padding: '8px 12px', border: '1px solid #e0e0e0', borderRadius: '8px',
  fontSize: '13px', outline: 'none', width: '100%', boxSizing: 'border-box',
  fontFamily: 'Segoe UI, sans-serif'
};
const labelStyle = { fontSize: '10px', color: '#888', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px', display: 'block' };

const fmt = (n) => (n == null || isNaN(n)) ? '—' : Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// IVA derivado del total (mismo algoritmo que el backend; aqui es informativo).
const deriveIva = (total, tasa) => {
  const t = parseFloat(total);
  if (isNaN(t)) return { sin: null, iva: null };
  const ta = parseFloat(tasa) || 0;
  const sin = Math.round((t / (1 + ta / 100)) * 100) / 100;
  return { sin, iva: Math.round((t - sin) * 100) / 100 };
};

export default function ModalGasto({ editando, catalogos, registradoPor, onClose, onSaved }) {
  const esEdicion = !!editando;
  const [form, setForm] = useState(esEdicion ? mapDesde(editando) : vacio());
  const [masDetalles, setMasDetalles] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');
  const [okMsg, setOkMsg] = useState('');
  const montoRef = useRef(null);

  useEffect(() => { if (montoRef.current) montoRef.current.focus(); }, []);

  function mapDesde(g) {
    return {
      fecha: (g.fecha || '').slice(0, 10) || hoy(),
      categoria_id: g.categoria_id || '', subcategoria_id: g.subcategoria_id || '',
      proyecto_id: g.proyecto_id || '', moneda: g.moneda || 'Colones',
      monto_total: g.monto_total != null ? g.monto_total : '', tasa_iva: g.tasa_iva ?? 13,
      descripcion: g.descripcion || '', proveedor: g.proveedor || '',
      tipo_comprobante: g.tipo_comprobante || '', numero_comprobante: g.numero_comprobante || '',
      empresa: g.empresa || 'JyA Conusa', estado_pago: g.estado_pago || 'Pendiente',
      medio_pago_id: g.medio_pago_id || '', fecha_pago: (g.fecha_pago || '').slice(0, 10), notas: g.notas || ''
    };
  }

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const cats = catalogos.categorias || [];
  const subsDeCat = (catalogos.subcategorias || []).filter(s => String(s.categoria_id) === String(form.categoria_id));
  const iva = deriveIva(form.monto_total, form.tasa_iva);

  const payload = () => ({
    ...form,
    categoria_id: parseInt(form.categoria_id),
    subcategoria_id: form.subcategoria_id ? parseInt(form.subcategoria_id) : null,
    proyecto_id: form.proyecto_id ? parseInt(form.proyecto_id) : null,
    medio_pago_id: form.medio_pago_id ? parseInt(form.medio_pago_id) : null,
    monto_total: form.monto_total === '' ? null : parseFloat(form.monto_total),
    tasa_iva: parseFloat(form.tasa_iva),
    registrado_por: registradoPor || null
  });

  const validar = () => {
    if (!form.fecha) return 'La fecha es requerida.';
    if (!form.categoria_id) return 'La categoria es requerida.';
    if (!form.moneda) return 'La moneda es requerida.';
    if (!form.empresa) return 'La empresa es requerida.';
    return '';
  };

  // Modo nuevo: POST + limpiar y seguir.
  const guardarYSeguir = async () => {
    const v = validar(); if (v) { setError(v); return; }
    setGuardando(true); setError(''); setOkMsg('');
    try {
      await axios.post(`${API}/gastos`, payload());
      onSaved && onSaved();
      // Conserva fecha, moneda, empresa, categoria; limpia el resto.
      setForm(f => ({
        ...vacio(),
        fecha: f.fecha, moneda: f.moneda, empresa: f.empresa,
        categoria_id: f.categoria_id, tasa_iva: f.tasa_iva
      }));
      setOkMsg('Gasto guardado. Listo para el siguiente.');
      if (montoRef.current) montoRef.current.focus();
    } catch (e) { setError('Error al guardar: ' + (e.response?.data?.error || e.message)); }
    finally { setGuardando(false); }
  };

  // Modo edicion: PUT + cerrar.
  const guardarCambios = async () => {
    const v = validar(); if (v) { setError(v); return; }
    setGuardando(true); setError('');
    try {
      await axios.put(`${API}/gastos?id=${editando.id}`, payload());
      onSaved && onSaved();
      onClose();
    } catch (e) { setError('Error al guardar: ' + (e.response?.data?.error || e.message)); }
    finally { setGuardando(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
      <div style={{ background: '#fff', borderRadius: '16px', width: '100%', maxWidth: '680px', maxHeight: '92vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

        <div style={{ background: '#012E40', padding: '16px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
          <span style={{ color: '#fff', fontSize: '15px', fontWeight: '700' }}>{esEdicion ? 'Editar gasto' : 'Nuevo gasto'}</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.7)', fontSize: '22px', cursor: 'pointer', lineHeight: 1 }}>✕</button>
        </div>

        <div style={{ overflowY: 'auto', padding: '24px' }}>
          {error && <div style={{ background: '#FCEBEB', border: '1px solid #f5c6c6', borderRadius: '8px', padding: '10px 14px', fontSize: '13px', color: '#A32D2D', marginBottom: '12px' }}>{error}</div>}
          {okMsg && !error && <div style={{ background: '#EAF3DE', border: '1px solid #b8dfc8', borderRadius: '8px', padding: '10px 14px', fontSize: '13px', color: '#3B6D11', marginBottom: '12px' }}>{okMsg}</div>}

          {/* Fila principal: lo minimo para registrar rapido */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div>
              <label style={labelStyle}>Fecha *</label>
              <input type="date" style={inputStyle} value={form.fecha} onChange={e => set('fecha', e.target.value)} />
            </div>
            <div>
              <label style={labelStyle}>Categoria *</label>
              <select style={inputStyle} value={form.categoria_id} onChange={e => { set('categoria_id', e.target.value); set('subcategoria_id', ''); }}>
                <option value="">— Seleccionar —</option>
                {cats.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Subcategoria</label>
              <select style={inputStyle} value={form.subcategoria_id} onChange={e => set('subcategoria_id', e.target.value)} disabled={!form.categoria_id}>
                <option value="">— (opcional) —</option>
                {subsDeCat.map(s => <option key={s.id} value={s.id}>{s.nombre}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Proyecto</label>
              <select style={inputStyle} value={form.proyecto_id} onChange={e => set('proyecto_id', e.target.value)}>
                <option value="">Sin proyecto (operativo)</option>
                {(catalogos.proyectos || []).map(p => <option key={p.id} value={p.id}>{p.id_proyecto} — {p.nombre}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Moneda *</label>
              <select style={inputStyle} value={form.moneda} onChange={e => set('moneda', e.target.value)}>
                {MONEDAS.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Monto total (del recibo)</label>
              <input ref={montoRef} type="number" step="0.01" style={inputStyle} value={form.monto_total}
                onChange={e => set('monto_total', e.target.value)} placeholder="0.00" />
            </div>
            <div>
              <label style={labelStyle}>IVA</label>
              <select style={inputStyle} value={form.tasa_iva} onChange={e => set('tasa_iva', e.target.value)}>
                {TASAS.map(t => <option key={t.v} value={t.v}>{t.t}</option>)}
              </select>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
              <div style={{ fontSize: '11px', color: '#888' }}>Base: <strong style={{ color: '#012E40' }}>{fmt(iva.sin)}</strong> · IVA: <strong style={{ color: '#012E40' }}>{fmt(iva.iva)}</strong></div>
            </div>
            <div style={{ gridColumn: '1/-1' }}>
              <label style={labelStyle}>Descripcion</label>
              <input style={inputStyle} value={form.descripcion} onChange={e => set('descripcion', e.target.value)} placeholder="Detalle del gasto (opcional)" />
            </div>
          </div>

          {/* Mas detalles (opcional) */}
          <button onClick={() => setMasDetalles(m => !m)}
            style={{ marginTop: '14px', background: 'none', border: 'none', color: '#185FA5', fontSize: '12px', fontWeight: '600', cursor: 'pointer', padding: 0 }}>
            {masDetalles ? '▾ Menos detalles' : '▸ Mas detalles (opcional)'}
          </button>

          {masDetalles && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #f0f0f0' }}>
              <div><label style={labelStyle}>Proveedor</label><input style={inputStyle} value={form.proveedor} onChange={e => set('proveedor', e.target.value)} /></div>
              <div>
                <label style={labelStyle}>Empresa *</label>
                <select style={inputStyle} value={form.empresa} onChange={e => set('empresa', e.target.value)}>
                  {EMPRESAS.map(x => <option key={x} value={x}>{x}</option>)}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Tipo de comprobante</label>
                <select style={inputStyle} value={form.tipo_comprobante} onChange={e => set('tipo_comprobante', e.target.value)}>
                  <option value="">— Ninguno —</option>
                  {TIPOS_COMPROBANTE.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div><label style={labelStyle}>Numero de comprobante</label><input style={inputStyle} value={form.numero_comprobante} onChange={e => set('numero_comprobante', e.target.value)} /></div>
              <div>
                <label style={labelStyle}>Estado de pago</label>
                <select style={inputStyle} value={form.estado_pago} onChange={e => set('estado_pago', e.target.value)}>
                  <option value="Pendiente">Pendiente</option>
                  <option value="Pagado">Pagado</option>
                </select>
              </div>
              <div>
                <label style={labelStyle}>Medio de pago</label>
                <select style={inputStyle} value={form.medio_pago_id} onChange={e => set('medio_pago_id', e.target.value)}>
                  <option value="">— Ninguno —</option>
                  {(catalogos.mediosPago || []).map(m => <option key={m.id} value={m.id}>{m.valor}</option>)}
                </select>
              </div>
              <div><label style={labelStyle}>Fecha de pago</label><input type="date" style={inputStyle} value={form.fecha_pago} onChange={e => set('fecha_pago', e.target.value)} /></div>
              <div style={{ gridColumn: '1/-1' }}><label style={labelStyle}>Notas</label><textarea style={{ ...inputStyle, minHeight: '60px', resize: 'vertical' }} value={form.notas} onChange={e => set('notas', e.target.value)} /></div>
            </div>
          )}
        </div>

        <div style={{ padding: '16px 24px', borderTop: '1px solid #eee', display: 'flex', justifyContent: 'flex-end', gap: '10px', flexShrink: 0 }}>
          <button onClick={onClose} style={{ padding: '9px 20px', border: '1px solid #e0e0e0', borderRadius: '8px', background: '#fff', cursor: 'pointer', fontSize: '13px' }}>
            {esEdicion ? 'Cancelar' : 'Salir'}
          </button>
          {esEdicion ? (
            <button onClick={guardarCambios} disabled={guardando}
              style={{ padding: '9px 24px', background: '#012E40', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', opacity: guardando ? 0.7 : 1 }}>
              {guardando ? 'Guardando...' : 'Guardar cambios'}
            </button>
          ) : (
            <button onClick={guardarYSeguir} disabled={guardando}
              style={{ padding: '9px 24px', background: '#87B749', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', opacity: guardando ? 0.7 : 1 }}>
              {guardando ? 'Guardando...' : 'Guardar y seguir'}
            </button>
          )}
        </div>

      </div>
    </div>
  );
}
